import React, { useCallback } from 'react'
import { usePopper } from 'react-popper'
import { RenderElementProps } from 'slate-react'
import { twJoin, twMerge } from 'tailwind-merge'
import { useHover } from 'shared/hooks/use-hover'
import { MentionElement } from 'modules/community/types/CustomEditor'

export interface CustomRenderMentionProps
  extends Partial<Pick<RenderElementProps, 'attributes' | 'children'>> {
  element: MentionElement
}

export const Mention = ({ attributes, children, element }: CustomRenderMentionProps) => {
  const { ref: hoverRef, hovered } = useHover<HTMLSpanElement>()
  const [referenceElement, setReferenceElement] = React.useState<null | HTMLSpanElement>(null)
  const [popperElement, setPopperElement] = React.useState<null | HTMLDivElement>(null)
  const { styles: popperStyles, attributes: popperAttributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: 'top-start',
      strategy: 'fixed',
    },
  )

  const setRef = useCallback(
    (node: HTMLSpanElement) => {
      // @ts-ignore
      // NOTE: some weird type inconsistency. This was of type `MutableRef` in the comments repo
      // here though it's somehow just a RefObject
      // while the code is the same
      hoverRef.current = node
      setReferenceElement(node)
    },
    [setReferenceElement],
  )

  return (
    <span ref={setRef}>
      <span
        {...attributes}
        className="inline-block align-baseline text-brand-primary no-underline hover:underline max-[750px]:max-w-full max-[750px]:break-words"
        contentEditable={false}
        data-cy={`mention-${element.displayName.replace(' ', '-')}`}
        data-avatar-url={element.profileImageUrl || ''}
        data-id={element.id || 0}
      >
        @{element.displayName}
        {children}
      </span>

      {hovered && (
        <div
          ref={setPopperElement}
          className="z-50"
          style={{ ...popperStyles.popper }}
          {...popperAttributes}
          contentEditable={false}
        >
          <div className="z-50 my-[5px] flex w-max max-w-[320px] items-center justify-center gap-[10px] overflow-hidden rounded-lg bg-background-secondary p-4 shadow-mention">
            {/* TODO: Delete when this task complete - https://app.shortcut.com/systeme/story/27961/extra-info-in-mentions-in-comments-response */}
            {element.profileImageUrl && (
              <img
                src={element.profileImageUrl}
                alt={element.displayName}
                width={46}
                height={46}
                className="size-[46px] rounded-full"
              />
            )}
            <p className="font-black">{element.displayName}</p>
          </div>
        </div>
      )}
    </span>
  )
}
